// frontend/features/clinic/clinicSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  // Paciente
  fetchPatientProfile,
  fetchPatientsList,
  fetchPatientById,
  updatePatientById,
  createPatient,
  deletePatientById,
  // Historial médico
  fetchMedicalHistory,
  createMedicalHistory,
  updateMedicalHistory,
  deleteMedicalHistory,
  // Alergias
  fetchAllergies,
  createAllergy,
  updateAllergy,
  deleteAllergy,
  // Medicación
  fetchMedications,
  createMedication,
  updateMedication,
  deleteMedication,
  // Cirugías
  fetchSurgeries,
  createSurgery,
  updateSurgery,
  deleteSurgery,
  // Recovery Notes
  fetchRecoveryNotes,
  createRecoveryNote,
  updateRecoveryNote,
  deleteRecoveryNote,

  // Casos clínicos globales (journey)
  fetchPatientCases,
  createPatientCase,
  updatePatientCase,
  deletePatientCase,

  fetchClinicalDocuments,
  uploadClinicalDocument,
  deleteClinicalDocument,
  downloadClinicalDocument,

  fetchPatientJourneyItems,
  createPatientJourneyItem,
  updatePatientJourneyItem,
  deletePatientJourneyItem,

} from "./clinicThunks.js";

const initialState = {
  list: [],
  pagination: { count: 0, next: null, previous: null, page: 1 },
  filters: { q: "", procedure: "" },
  patientData: null,
  medicalHistory: [],
  allergies: [],
  medications: [],
  surgeries: [],
  recoveryNotes: [],

  // 👉 Casos clínicos globales (seguimiento por episodio)
  patientCases: [],
  patientCasesPagination: { count: 0, next: null, previous: null, page: 1 },
  patientCasesFilters: { patientId: null },

  clinicalDocuments: [],
  clinicalDocumentsPagination: {
    count: 0,
    next: null,
    previous: null,
    page: 1,
  },
  clinicalDocumentsFilters: { patientId: null },
  // 👉 Recorrido del paciente (línea del tiempo por fases)
  patientJourneyItems: [],
  patientJourneyPagination: { count: 0, next: null, previous: null, page: 1 },
  patientJourneyFilters: {
    patientId: null,
    courseId: null,
    phase: "",
    area: "",
    status: "",
  },
  loading: false,
  error: null,
};

function upsert(arr, item) {
  if (!item || item.id == null) return;

  // Evitar crash si alguien pasa algo que no es array
  if (!Array.isArray(arr)) {
    // opcional: log para localizar quién la está liando
    // eslint-disable-next-line no-console
    console.warn("upsert llamado con algo que no es array:", arr);
    return;
  }

  const idx = arr.findIndex((x) => x.id === item.id);
  if (idx >= 0) arr[idx] = item;
  else arr.unshift(item);
}

function removeById(arr, id) {
  const idx = arr.findIndex((x) => x.id === id);
  if (idx >= 0) arr.splice(idx, 1);
}

// Mantiene patientData.surgeries[*].recovery_notes en sync si existe
function upsertRecoveryInPatientData(patientData, note) {
  if (!patientData?.surgeries || !note?.surgery) return;
  const sIdx = patientData.surgeries.findIndex((s) => s.id === note.surgery);
  if (sIdx < 0) return;
  const surgery = patientData.surgeries[sIdx];
  surgery.recovery_notes = Array.isArray(surgery.recovery_notes) ? surgery.recovery_notes : [];
  upsert(surgery.recovery_notes, note);
}

function removeRecoveryInPatientData(patientData, noteId) {
  if (!patientData?.surgeries) return;
  for (const s of patientData.surgeries) {
    if (Array.isArray(s.recovery_notes)) {
      removeById(s.recovery_notes, noteId);
    }
  }
}

const clinicSlice = createSlice({
  name: "clinic",
  initialState,
  reducers: {
    resetClinicState: () => initialState,
  },
  extraReducers: (builder) => {
    /* ========== Paciente ========== */
    builder
      .addCase(fetchPatientProfile.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchPatientProfile.fulfilled, (state, action) => {
        state.loading = false; state.patientData = action.payload;
      })
      .addCase(fetchPatientProfile.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(fetchPatientsList.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchPatientsList.fulfilled, (state, action) => {
        state.loading = false;
        const { results = [], count = 0, next = null, previous = null, page = 1, q = "", procedure = "" } = action.payload || {};
        state.list = results; state.pagination = { count, next, previous, page }; state.filters = { q, procedure };
      })
      .addCase(fetchPatientsList.rejected, (state, action) => {
        state.loading = false; state.error = action.payload || "Error al obtener pacientes.";
      });

    builder
      .addCase(fetchPatientById.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchPatientById.fulfilled, (state, action) => {
        state.loading = false; state.patientData = action.payload || null;
      })
      .addCase(fetchPatientById.rejected, (state, action) => {
        state.loading = false; state.error = action.payload || "Error al leer paciente.";
      });

    builder
      .addCase(updatePatientById.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updatePatientById.fulfilled, (state, action) => {
        state.loading = false; state.patientData = action.payload || state.patientData;
      })
      .addCase(updatePatientById.rejected, (state, action) => {
        state.loading = false; state.error = action.payload || "Error al actualizar los datos.";
      });

    builder
      .addCase(deletePatientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePatientById.fulfilled, (state, action) => {
        state.loading = false;

        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.list, id);
          if (state.patientData?.id === id) state.patientData = null;

          if (state.pagination?.count != null && state.pagination.count > 0) {
            state.pagination.count -= 1;
          }
        }
      })
      .addCase(deletePatientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al eliminar paciente.";
      });

    /* ========== Historial Médico ========== */
    builder
      .addCase(fetchMedicalHistory.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchMedicalHistory.fulfilled, (state, action) => {
        state.loading = false; state.medicalHistory = action.payload || [];
      })
      .addCase(fetchMedicalHistory.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(createMedicalHistory.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(createMedicalHistory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.medicalHistory, action.payload);
          if (state.patientData?.medical_conditions) {
            upsert(state.patientData.medical_conditions, action.payload);
          }
        }
      })
      .addCase(createMedicalHistory.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(updateMedicalHistory.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updateMedicalHistory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.medicalHistory, action.payload);
          if (state.patientData?.medical_conditions) {
            upsert(state.patientData.medical_conditions, action.payload);
          }
        }
      })
      .addCase(updateMedicalHistory.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(deleteMedicalHistory.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(deleteMedicalHistory.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.medicalHistory, id);
          if (state.patientData?.medical_conditions) {
            removeById(state.patientData.medical_conditions, id);
          }
        }
      })
      .addCase(deleteMedicalHistory.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    /* ========== Alergias ========== */
    builder
      .addCase(fetchAllergies.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchAllergies.fulfilled, (state, action) => {
        state.loading = false; state.allergies = action.payload || [];
      })
      .addCase(fetchAllergies.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(createAllergy.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(createAllergy.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.allergies, action.payload);
          if (state.patientData?.allergies) {
            upsert(state.patientData.allergies, action.payload);
          }
        }
      })
      .addCase(createAllergy.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(updateAllergy.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updateAllergy.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.allergies, action.payload);
          if (state.patientData?.allergies) {
            upsert(state.patientData.allergies, action.payload);
          }
        }
      })
      .addCase(updateAllergy.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(deleteAllergy.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(deleteAllergy.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.allergies, id);
          if (state.patientData?.allergies) {
            removeById(state.patientData.allergies, id);
          }
        }
      })
      .addCase(deleteAllergy.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    /* ========== Medicación ========== */
    builder
      .addCase(fetchMedications.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchMedications.fulfilled, (state, action) => {
        state.loading = false; state.medications = action.payload || [];
      })
      .addCase(fetchMedications.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(createMedication.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(createMedication.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.medications, action.payload);
          if (state.patientData?.medications) {
            upsert(state.patientData.medications, action.payload);
          }
        }
      })
      .addCase(createMedication.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(updateMedication.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updateMedication.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.medications, action.payload);
          if (state.patientData?.medications) {
            upsert(state.patientData.medications, action.payload);
          }
        }
      })
      .addCase(updateMedication.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(deleteMedication.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(deleteMedication.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.medications, id);
          if (state.patientData?.medications) {
            removeById(state.patientData.medications, id);
          }
        }
      })
      .addCase(deleteMedication.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    /* ========== Cirugías ========== */
    builder
      .addCase(fetchSurgeries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSurgeries.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];

        state.surgeries = list;

        // opcional: mantener en sync patientData.surgeries
        if (state.patientData) {
          state.patientData.surgeries = list;
        }
      })
      .addCase(fetchSurgeries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.surgeries = [];
      });

    builder
      .addCase(createSurgery.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(createSurgery.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.surgeries, action.payload);
          if (state.patientData?.surgeries) {
            upsert(state.patientData.surgeries, action.payload);
          }
        }
      })
      .addCase(createSurgery.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(updateSurgery.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updateSurgery.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.surgeries, action.payload);
          if (state.patientData?.surgeries) {
            upsert(state.patientData.surgeries, action.payload);
          }
        }
      })
      .addCase(updateSurgery.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(deleteSurgery.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(deleteSurgery.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.surgeries, id);
          if (state.patientData?.surgeries) {
            removeById(state.patientData.surgeries, id);
          }
        }
      })
      .addCase(deleteSurgery.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

    /* ========== Recovery Notes ========== */
    builder
      .addCase(fetchRecoveryNotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecoveryNotes.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];

        state.recoveryNotes = list;
      })
      .addCase(fetchRecoveryNotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.recoveryNotes = [];
      });

    builder
      .addCase(createRecoveryNote.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(createRecoveryNote.fulfilled, (state, action) => {
        state.loading = false;
        const note = action.payload;
        if (note) {
          upsert(state.recoveryNotes, note);
          if (state.patientData) upsertRecoveryInPatientData(state.patientData, note);
        }
      })
      .addCase(createRecoveryNote.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(updateRecoveryNote.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updateRecoveryNote.fulfilled, (state, action) => {
        state.loading = false;
        const note = action.payload;
        if (note) {
          upsert(state.recoveryNotes, note);
          if (state.patientData) upsertRecoveryInPatientData(state.patientData, note);
        }
      })
      .addCase(updateRecoveryNote.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    builder
      .addCase(deleteRecoveryNote.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(deleteRecoveryNote.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.recoveryNotes, id);
          if (state.patientData) removeRecoveryInPatientData(state.patientData, id);
        }
      })
      .addCase(deleteRecoveryNote.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });

    /* ========== Patient Cases (casos clínicos globales) ========== */
    builder
      .addCase(fetchPatientCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientCases.fulfilled, (state, action) => {
        state.loading = false;
        const {
          results = [],
          count = 0,
          next = null,
          previous = null,
          page = 1,
          patientId = null,
        } = action.payload || {};

        state.patientCases = Array.isArray(results) ? results : [];
        state.patientCasesPagination = { count, next, previous, page };
        state.patientCasesFilters = { patientId };
      })
      .addCase(fetchPatientCases.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error al cargar los casos clínicos.";
        state.patientCases = [];
      });

    builder
      .addCase(createPatientCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPatientCase.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.patientCases, action.payload);
          // Si en un futuro añades `patientData.cases`, aquí podríamos mantenerlo en sync
          // if (state.patientData?.cases) {
          //   upsert(state.patientData.cases, action.payload);
          // }
        }
      })
      .addCase(createPatientCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al crear el caso clínico.";
      });

    builder
      .addCase(updatePatientCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatientCase.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.patientCases, action.payload);
          // Igual que arriba: si en el serializer incluyes casos dentro del paciente,
          // aquí se puede sincronizar también.
          // if (state.patientData?.cases) {
          //   upsert(state.patientData.cases, action.payload);
          // }
        }
      })
      .addCase(updatePatientCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al actualizar el caso clínico.";
      });

    builder
      .addCase(deletePatientCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePatientCase.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.patientCases, id);
          // Si luego guardas casos también dentro de patientData:
          // if (state.patientData?.cases) {
          //   removeById(state.patientData.cases, id);
          // }
        }
      })
      .addCase(deletePatientCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al eliminar el caso clínico.";
      });

    /* ========== Clinical Documents ========== */
    builder
      .addCase(fetchClinicalDocuments.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchClinicalDocuments.fulfilled, (state, action) => {
        state.loading = false;
        const { results = [], count = 0, next = null, previous = null, page = 1, patientId = null } = action.payload || {};
        state.clinicalDocuments = results;
        state.clinicalDocumentsPagination = { count, next, previous, page };
        state.clinicalDocumentsFilters = { patientId };
      })
      .addCase(fetchClinicalDocuments.rejected, (state, action) => {
        state.loading = false; state.error = action.payload || "Error al cargar documentos.";
      });

    builder
      .addCase(uploadClinicalDocument.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(uploadClinicalDocument.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.clinicalDocuments, action.payload);
        }
      })
      .addCase(uploadClinicalDocument.rejected, (state, action) => {
        state.loading = false; state.error = action.payload || "Error al subir documento.";
      });

    builder
      .addCase(deleteClinicalDocument.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(deleteClinicalDocument.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) removeById(state.clinicalDocuments, id);
      })
      .addCase(deleteClinicalDocument.rejected, (state, action) => {
        state.loading = false; state.error = action.payload || "Error al borrar documento.";
      });

    builder
      .addCase(createPatient.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(createPatient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPatient.rejected, (state, action) => {
        state.loading = false; state.error = action.payload || "Error al crear paciente.";
      });

    /* ========== Patient Journey (línea del tiempo por fases) ========== */
    builder
      .addCase(fetchPatientJourneyItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientJourneyItems.fulfilled, (state, action) => {
        state.loading = false;

        const {
          results = [],
          count = 0,
          next = null,
          previous = null,
          page = 1,
          patientId = null,
          courseId = null,
          phase = "",
          area = "",
          status = "",
        } = action.payload || {};

        state.patientJourneyItems = Array.isArray(results) ? results : [];
        state.patientJourneyPagination = { count, next, previous, page };
        state.patientJourneyFilters = { patientId, courseId, phase, area, status };
      })
      .addCase(fetchPatientJourneyItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al cargar el seguimiento por fases.";
        state.patientJourneyItems = [];
      });

    builder
      .addCase(createPatientJourneyItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPatientJourneyItem.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.patientJourneyItems, action.payload);
        }
      })
      .addCase(createPatientJourneyItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al crear el ítem del seguimiento.";
      });

    builder
      .addCase(updatePatientJourneyItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatientJourneyItem.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          upsert(state.patientJourneyItems, action.payload);
        }
      })
      .addCase(updatePatientJourneyItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al actualizar el ítem del seguimiento.";
      });

    builder
      .addCase(deletePatientJourneyItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePatientJourneyItem.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload || {};
        if (id != null) {
          removeById(state.patientJourneyItems, id);
          if (state.patientJourneyPagination?.count != null && state.patientJourneyPagination.count > 0) {
            state.patientJourneyPagination.count -= 1;
          }
        }
      })
      .addCase(deletePatientJourneyItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error al eliminar el ítem del seguimiento.";
      });
  },
});

export const { resetClinicState } = clinicSlice.actions;
export default clinicSlice.reducer;